"use client"
import { Alert, Button, Checkbox, ConfigProvider, DatePicker, Form, Input, InputNumber, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { UnitTypeModel } from "@/core/models/unit-type/unit-type.model";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { NOTIFICATION_METHODS, notificationMethodPipe } from "@/core/pipes/notification-method.pipe";
import { PAYMENT_METHODS, paymentMethodPipe } from "@/core/pipes/payment-method-pipe";
import { usePermissionStore } from "@/core/stores/permission.store";
import { WaterUserDepositStatus, WaterUserStatus } from "@/core/enums/global.enums";
import { contractStatusPipe } from "@/core/pipes/contract-status.pipe";
import { currencyFormatter } from "@/core/pipes/format-currency.pipe";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";
import { useDrafts } from "@/core/stores/drafts.store";
import { SyncOutlined } from "@ant-design/icons";
import { UploadForm, UploadFormHandle } from "@/app/home/components/upload/UploadForm";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { deepCompare, DEF_DATE_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import dayjs from "dayjs";
import { ResellerDiscountPolicySection } from "../components/ResellerDiscountPolicySection";

import { useCustomerCodeRuntime } from '../hooks/useCustomerCode';
import { CustomerCodeFields } from '../components/CustomerCodeFields';
import { useFactoryRuntimeSettings } from '@/core/stores/factory-runtime-settings.store';
import { CustomerCodeDraftSession, newCustomerCodeRequestId, prepareCustomerCodeCreate, PendingAutomaticCustomerCreate, snapshotPendingCustomerCodeCreate } from '@/core/utils/customer-code';
import TokenRepo from '@/core/repos/token.repo';

const contractRepo = container.get(ContractRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const unitTypeRepo = container.get(UnitTypeRepo);

interface CreateModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: ContractModel,
}

const INPUT_WIDTH = 450;
const DATE_FIELDS: (keyof ContractModel)[] = [
    "lastReadDate",
    "meterSetupDate",
    "identityDate",
    "collectionDate",
    "createdAt",
    "updatedAt",
];

const normalizeDateFields = (payload: Partial<ContractModel>) => {
    DATE_FIELDS.forEach((field) => {
        const value = payload[field];
        if (value === "" || value === null) {
            delete payload[field];
            return;
        }
        if (dayjs.isDayjs(value)) {
            (payload as Record<string, string>)[field] = value.toISOString();
        }
    });
    return payload;
};

export const DetailModal: React.FC<CreateModalProps> = (props) => {
    const context = useFactoryRuntimeSettings();
    return <CustomerDetailModal key={`${context.factoryId}:${context.contextId}:${props.data.id}`} {...props} />;
};
const CustomerDetailModal: React.FC<CreateModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const context = useFactoryRuntimeSettings();
    const runtime = useCustomerCodeRuntime(isModalOpen && !data.id);
    const autoCode = !!runtime.configuration?.enabled;
    const draftKey = `ContractModel:${context.factoryId || ''}`;
    const pendingKey = `${draftKey}:pendingAutomatic`;
    const storedPending = useDrafts(state => state.getDraft(pendingKey)) as PendingAutomaticCustomerCreate<ContractModel> | undefined;
    const pendingAutomatic = data.id ? undefined : storedPending;
    const automaticCreate = autoCode || !!pendingAutomatic;
    const automaticConfiguration = pendingAutomatic?.configuration || runtime.configuration;
    const submitSession = useRef<CustomerCodeDraftSession | undefined>(undefined);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);
    const sessionToken = new TokenRepo().getToken();
    const sessionCurrent = () => mounted.current && new TokenRepo().getToken() === sessionToken;

    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<ContractModel>();
    // Draft logic for ContractModel
    const draft = useDrafts((state) => state.getDraft(draftKey));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft(draftKey, form.getFieldsValue(true));
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft(draftKey);
        _clearDraft(pendingKey);
        submitSession.current = undefined;
        form.setFieldsValue({
            ...data,
            clientRequestId: autoCode ? newCustomerCodeRequestId() : undefined,
            villageId: undefined,
            communeId: undefined,
            doNotIssueEInvoice: data.doNotIssueEInvoice ?? false,
            isWaterReseller: data.isWaterReseller ?? false,
        });
    };
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [unitTypes, setUnitTypes] = useState<UnitTypeModel[]>([]);
    const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
    const uploadFormRef = useRef<UploadFormHandle>(null); 
    //Attach hooks to handle data changes
    const handleDataChange = async () => {
        data.paymentMethodArray = GlobalUtils.parseArray(data.paymentMethod);
        data.notificationMethodArray = GlobalUtils.parseArray(data.notificationMethod);
        form.setFieldsValue({
            ...data,
            doNotIssueEInvoice: data.doNotIssueEInvoice ?? false,
            isWaterReseller: data.isWaterReseller ?? false,
        });
        if (data.id&&data.id.length>0) {
            setIsEdit(true);
        } else {
            if (draft || pendingAutomatic) form.setFieldsValue({...pendingAutomatic?.payload, ...draft, ...(pendingAutomatic ? {clientRequestId:pendingAutomatic.requestId} : {})});
            setIsEdit(false);
        }
    }
    useEffect(()=>{
        handleDataChange();
    }, [data])

    useEffect(() => {
        if (!data.id && automaticCreate) {
            const requestId = pendingAutomatic?.requestId || form.getFieldValue('clientRequestId') || newCustomerCodeRequestId();
            form.setFieldValue('clientRequestId', requestId);
            if (submitSession.current?.requestId !== requestId) submitSession.current = new CustomerCodeDraftSession(requestId);
        }
    }, [automaticCreate, pendingAutomatic?.requestId, data.id, form]);
    useEffect(() => {
        if (!runtime.revision || useDrafts.getState().getDraft(pendingKey)) return;
        _clearDraft(draftKey);
        submitSession.current = undefined;
        form.setFieldsValue({clientRequestId: undefined, communeId: undefined, villageId: undefined});
    }, [runtime.revision]);

    async function populateSelectOptions() {
        const [_tollAreas, _unitTypes] = await Promise.all([
            tollAreaRepo.getAll(),
            unitTypeRepo.getAll()
        ]);
        setUnitTypes(_unitTypes);
        setTollAreas(_tollAreas);
    }

    async function init() {
        await populateSelectOptions();
    }

    useEffect(()=>{init()}, [])

    const handleSave = async () => {
        if (!isEdit && !runtime.ready && !pendingAutomatic) { message.error(runtime.error || 'Đang tải cấu hình mã khách hàng.'); return; }
        const requestId = pendingAutomatic?.requestId || (autoCode ? (form.getFieldValue('clientRequestId') || newCustomerCodeRequestId()) : '');
        if (!submitSession.current || submitSession.current.requestId !== requestId) submitSession.current = new CustomerCodeDraftSession(requestId);
        const submission = submitSession.current;
        if (automaticCreate) form.setFieldValue('clientRequestId', submission.requestId);
        if (!submission.begin()) return;
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (!sessionCurrent()) return;
            if (formData.paymentMethodArray&&formData.paymentMethodArray.length>0) {
                formData.paymentMethod = formData.paymentMethodArray[0];
            } else {
                formData.paymentMethod = "";
            }

            if (formData.notificationMethodArray&&formData.notificationMethodArray.length>0) {
                formData.notificationMethod = formData.notificationMethodArray.join(";")+";"
            } else {
                formData.notificationMethod = "";
            }

            if (isEdit) {
                formData.id = data.id;
                const payload = normalizeDateFields({ ...data, ...formData });
                const result = await contractRepo.update(payload);
                if (result) {
                    await handleUpload(data.id);
                    setIsModalOpen(false);
                    message.success({
                        content: "Đã cập nhật Hợp đồng"
                    })
                } else {
                    handleUploadReset();
                    message.error({
                        content: "Cập nhật hợp đồng thất bại"
                    })
                }
            } else {
                const payload = { ...(pendingAutomatic?.payload || data), ...formData };
                const prepared = normalizeDateFields(prepareCustomerCodeCreate(payload, autoCode, submission.requestId, pendingAutomatic)) as ContractModel;
                if (automaticCreate) {
                    _saveDraft(draftKey, { ...form.getFieldsValue(true), clientRequestId: submission.requestId });
                    _saveDraft(pendingKey, snapshotPendingCustomerCodeCreate(prepared, submission.requestId, automaticConfiguration));
                }
                const result = await contractRepo.create(prepared);
                if (!sessionCurrent()) return;
                if (result) {
                    await handleUpload(result.id);
                    clearDraft();
                    setIsModalOpen(false);
                    message.success({
                        content: "Thêm hợp đồng mới thành công"
                    })
                } else {
                    handleUploadReset();
                    message.error({
                        content: "Thêm hợp đồng mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        finally { submission.finish(); setLoading(false); }
    };
    const handleCancel = () => {
        if (!isEdit) saveDraft();
        setIsModalOpen(false);
    };

    const handleUnitTypeChange = (recordId: string) => {
        const record = unitTypes.find(type => type.id === recordId);
        if (record) {
            form.setFieldValue("requiredDepositAmount", record.requiredDepositAmount);
            
        }
    }

    const generateCode = async () => {
        if (automaticCreate || !runtime.ready) return;
        const tollAreaId = form.getFieldValue("tollAreaId");
        if (tollAreaId) {
            const payload = await contractRepo.generateCode(tollAreaId);
            if (payload) {
                form.setFieldsValue({
                    "code": payload.code,
                    "identityCode": payload.identityCode
                });
            } else {
                message.error("Xảy ra lỗi khi tạo mã!");
            }
        } else {
            message.error("Chưa chọn khu vực!");
        }
    }

    const resetCode = async () => {
        form.setFieldsValue({
            "code": undefined,
            "identityCode": undefined
        });
    }

    const handleUpload = async (referenceId: string) => {
        if (uploadFormRef.current) await uploadFormRef.current.saveUploadedFiles(referenceId);
    }

    const handleUploadReset = () => {
        if (uploadFormRef.current) uploadFormRef.current.reset();
    }

    return (
        <Modal open={isModalOpen} onCancel={handleCancel} width={1000} style={{ top: 20 }}
            styles={{
                mask: {
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'var(--primary-mask)'
                }
            }}
            footer={[
                <div key={1} className="w-full flex flex-row justify-end px-1.5">
                    <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                </div>,
            ]}
        >
            <ConfigProvider theme={MODAL_THEME}>
            <div className="flex flex-row w-full items-center justify-start select-none">
                <p className="ml-2 text-2xl font-bold text-primary">{isEdit?"CHI TIẾT KHÁCH HÀNG":"TẠO KHÁCH HÀNG"}</p>
                {!isEdit && <ResetDraftButton onClick={clearDraft} />}
            </div>
            <div className="flex flex-row justify-center py-4">
                {!isEdit && !runtime.ready && !pendingAutomatic && <Alert className="mb-4" type={runtime.error?'error':'info'} showIcon message={runtime.error || 'Đang tải cấu hình mã khách hàng…'} />}
                <Form<ContractModel>
                    name="basic"
                    className="flex flex-col"
                    autoComplete="off"
                    labelAlign="right"
                    layout="vertical"
                    form={form}
                    disabled={loading || (isEdit&&!permissionStore.page.edit)}
                >
                    <p className="text-xl font-bold text-primary my-1">1. THÔNG TIN HỢP ĐỒNG</p>
                    <div className="grid grid-cols-2 gap-x-4 items-end">
                        <Form.Item<ContractModel>
                            label="Người đại diện"
                            className="col-span-2"
                            name="name"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input placeholder="Nhập Người đại diện" style={{width: "100%"}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Loại hình đơn vị"
                            name="unitTypeId"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select showSearch placeholder="Chọn Loại hình đơn vị" style={{width: INPUT_WIDTH}} onChange={handleUnitTypeChange}>
                            {unitTypes.map(opt => <Select.Option key={opt.id} value={opt.id}>{opt.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Khu vực"
                            name="tollAreaId"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                            options={tollAreas.map(area => ({value: area.id, label: area.name}))}
                            placeholder="Nhập Khu vực" 
                            style={{width: INPUT_WIDTH}} 
                            onChange={() => {if (!isEdit) resetCode();}}></Select>
                        </Form.Item>
                        {!isEdit && automaticCreate ? (automaticConfiguration ? <CustomerCodeFields form={form} configuration={automaticConfiguration} previewEnabled={!pendingAutomatic} /> : <Alert type="info" message="Đang giữ yêu cầu tạo tự động trước đó để thử lại." />) : <>
                        <Form.Item<ContractModel>
                            label="Mã hợp đồng"
                            name="code"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input placeholder="Nhập Mã hợp đồng" style={{width: INPUT_WIDTH}} disabled={isEdit}
                                addonAfter={
                                    !isEdit&&<Button type="text" style={{height: 30, color: "var(--primary-color)"}} icon={<SyncOutlined />} onClick={generateCode}></Button>
                                }
                            />
                        </Form.Item>
                        </>}
                        <Form.Item<ContractModel>
                            label="Tài khoản đại diện"
                            name="userName"
                        >
                            <Input placeholder="Nhập Tài khoản đại diện" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Địa chỉ"
                            className="col-span-2"
                            name="address"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input placeholder="Nhập Địa chỉ" style={{width: "100%"}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Trạng thái hoạt động"
                            name="status"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select showSearch placeholder="Chọn trạng thái" style={{width: INPUT_WIDTH}}>
                            {Object.keys(WaterUserStatus).map(opt => <Select.Option key={opt} value={opt}>{contractStatusPipe(opt)}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Hóa đơn điện tử"
                            name="doNotIssueEInvoice"
                            valuePropName="checked"
                        >
                            <Checkbox>Không phát hành hóa đơn điện tử</Checkbox>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            name="isWaterReseller"
                            valuePropName="checked"
                            className="col-span-2"
                        >
                            <Checkbox>Doanh nghiệp mua nước để bán lại</Checkbox>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Trạng thái thu cọc"
                            name="depositStatus"
                        >
                            <Select placeholder="Chọn trạng thái" style={{width: INPUT_WIDTH}} disabled>
                                {Object.keys(WaterUserDepositStatus).map(opt => <Select.Option key={opt} value={opt}>{depositStatusPipe(opt)}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Số tiền phải cọc"
                            name="requiredDepositAmount"
                        >
                            <InputNumber controls={false} formatter={currencyFormatter} placeholder="Nhập Số tiền phải cọc" style={{width: INPUT_WIDTH}} disabled/>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Số tiền đã nộp"
                            name="depositAmount"
                        >
                            <InputNumber controls={false} formatter={currencyFormatter} placeholder="Nhập Số tiền đã nộp" style={{width: INPUT_WIDTH}} disabled/>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Người thu tiền"
                            name="collector"
                        >
                            <Input placeholder="Nhập Người thu tiền" style={{width: INPUT_WIDTH}} disabled/>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Ngày thu"
                            name="collectionDate"
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Ngày thu" style={{width: INPUT_WIDTH}} disabled></DatePicker>
                        </Form.Item>
                    </div>
                    <div className="mt-1 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                    <p className="text-xl font-bold text-primary my-1">2. THÔNG TIN KHÁCH HÀNG</p>
                    <div className="grid grid-cols-2 gap-x-4 items-end">
                        <Form.Item<ContractModel>
                            label="Tên đơn vị/ tổ chức"
                            name="organization"
                        >
                            <Input placeholder="Nhập Tên đơn vị/ tổ chức" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="CMND/CCCD"
                            name="identityNo"
                        >
                            <Input placeholder="Nhập CMND/CCCD" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Mã số thuế"
                            name="taxCode"
                        >
                            <Input placeholder="Nhập Mã số thuế" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Ngày cấp CCCD"
                            name="identityDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày cấp CCCD" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Điện thoại"
                            name="phone"
                        >
                            <Input placeholder="Nhập Điện thoại" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Nơi cấp"
                            name="identityProPlace"
                        >
                            <Input placeholder="Nhập Nơi cấp" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Tỷ lệ hỗ trợ (19/2020/NQ-HĐND)"
                            name="supportCashRate"
                        >
                            <Input placeholder="Nhập Tỷ lệ hỗ trợ (19/2020/NQ-HĐND)" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Địa chỉ thường trú"
                            name="identityPlace"
                        >
                            <Input placeholder="Nhập Địa chỉ thường trú" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Email"
                            name="email"
                        >
                            <Input placeholder="Nhập Email" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Số khẩu"
                            name="numberOfPeople"
                        >
                            <Input placeholder="Nhập Số khẩu" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                    </div>
                    <div className="mt-1 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                    <p className="text-xl font-bold text-primary my-1">3. THÔNG TIN ĐỒNG HỒ</p>
                    <div className="grid grid-cols-2 gap-x-4 items-end">
                        <Form.Item<ContractModel>
                            label="Đồng hồ đo nước hiệu"
                            name="waterMeterName"
                        >
                            <Input placeholder="Nhập Đồng hồ đo nước hiệu" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Đồng hồ đo nước loại"
                            name="waterMeterType"
                        >
                            <Input placeholder="Nhập Đồng hồ đo nước loại" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Mã đồng hồ nước"
                            name="waterMeterCode"
                        >
                            <Input placeholder="Nhập Mã đồng hồ nước" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Chỉ số cuối"
                            name="lastWaterMeterNumber"
                        >
                            <Input placeholder="Nhập Chỉ số cuối" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Ngày đọc cuối"
                            name="lastReadDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày đọc cuối" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Ngày lắp đồng hồ"
                            name="meterSetupDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày lắp đồng hồ" style={{width: INPUT_WIDTH}} />
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Chỉ số tối đa đồng hồ"
                            name="waterMeterMaxIndex"
                        >
                            <InputNumber 
                                controls={false} 
                                placeholder="Nhập Chỉ số tối đa đồng hồ (mặc định: 9999)" 
                                style={{width: INPUT_WIDTH}}
                                min={0}
                            />
                        </Form.Item>
                        <div className="col-span-2">
                            <Form.Item<ContractModel>
                                label="Địa chỉ Gps"
                                name="gps"
                            >
                                <Input placeholder="Nhập Địa chỉ Gps" style={{width: "100%"}} />
                            </Form.Item>
                        </div>
                    </div>
                    <UploadForm 
                        ref={uploadFormRef}
                        objectType={"WaterUser"}
                        referenceId={data.id.length > 0?data.id:undefined}
                        text="Ảnh">
                    </UploadForm>
                    <div className="mt-4 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                    <p className="text-xl font-bold text-primary my-1">4. THÔNG TIN THANH TOÁN</p>
                    <div className="grid grid-cols-2 gap-x-4 items-end">
                        <Form.Item<ContractModel>
                            label="Hình thức thông báo"
                            name="notificationMethodArray"
                        >
                            <Select showSearch mode="multiple" style={{width: INPUT_WIDTH}}>
                            {NOTIFICATION_METHODS.map(opt => <Select.Option key={opt} value={opt}>{notificationMethodPipe(opt)}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Hình thức thanh toán"
                            name="paymentMethodArray"
                        >
                            <Select showSearch mode="multiple" maxCount={1} style={{width: INPUT_WIDTH}}>
                            {PAYMENT_METHODS.map(opt => <Select.Option key={opt} value={opt}>{paymentMethodPipe(opt)}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Ngân hàng"
                            name="bank"
                        >
                            <Input placeholder="Nhập Ngân hàng" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<ContractModel>
                            label="Số tài khoản"
                            name="bankNo"
                        >
                            <Input placeholder="Nhập Số tài khoản" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                    </div>
                    <div className="mt-1 mb-4 mx-0 border-t border-dashed border-gray-300"></div>
                    <p className="text-xl font-bold text-primary my-1">5. THÔNG TIN BỔ SUNG</p>
                    <div className="grid grid-cols-2 gap-x-4 items-end">
                        <div className="col-span-2">
                            <Form.Item<ContractModel>
                                label="Ghi chú"
                                name="note"
                            >
                                <Input.TextArea placeholder="Nhập ghi chú" style={{width: "100%", height: 202, resize: "none", marginRight: 0}}></Input.TextArea>

                            </Form.Item>
                        </div>
                        {isEdit&&<>
                            <Form.Item<ContractModel>
                                label="Ngày tạo"
                                name="createdAt"
                                getValueProps={(i) => ({value: dayjsPipe(i)})}
                            >
                                <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày tạo" style={{width: INPUT_WIDTH}} disabled></DatePicker>
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Ngày cập nhật"
                                name="updatedAt"
                                getValueProps={(i) => ({value: dayjsPipe(i)})}
                            >
                                <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày cập nhật" style={{width: INPUT_WIDTH}} disabled></DatePicker>
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Người tạo"
                                name="createdBy"
                            >
                                <Input placeholder="Nhập Người tạo" style={{width: INPUT_WIDTH}} disabled></Input>
                            </Form.Item>
                            <Form.Item<ContractModel>
                                label="Ngày tạo"
                                name="createdAt"
                                getValueProps={(i) => ({value: dayjsPipe(i)})}
                            >
                                <DatePicker format={DEF_DATE_FORMAT}placeholder="Nhập Ngày tạo" style={{width: INPUT_WIDTH}} disabled></DatePicker>
                            </Form.Item>
                        </>}
                    </div>
                </Form>
            </div>
            <ResellerDiscountPolicySection
                key={data.id || "new-customer"}
                waterUserId={data.id || ""}
                isWaterReseller={data.isWaterReseller ?? false}
                enabled={isModalOpen}
            />
            </ConfigProvider>
            <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
        </Modal>
    )
}
